import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { DateHelperService } from '../../services/date-helper.service';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';

@Component({
  selector: 'formly-field-date-range',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h3>
      {{ props.label }}
      <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
    </h3>
    @if (field.props.description) {
      <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
    }
    <div class="flex flex-row gap-4 w-full">
      @for (field of field.fieldGroup; track field.id; let i = $index) {
        <formly-field [field]="field" [class]="field.props.className"></formly-field>
        @if (i === 0 && this.props.maximalDaysRange === 1) {
          <div class="flex-0 leading-10">-</div>
        }
      }
    </div>
    @if (this.props?.maximalDaysRange > 1) {
      <mat-hint>Maximaal aantal dagen is {{ this.props.maximalDaysRange }}</mat-hint>
    }
    <app-formly-error [field]="field"></app-formly-error>
    @if (offset < 0) {
      <mat-error class="pb-4">Let op: De begindatum moet eerder zijn dan de einddatum.</mat-error>
    }
  `,
  imports: [
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FormlyModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
  ],
})
export class FormlyFieldDateRangeComponent extends FieldType implements OnInit {
  private destroy$ = injectDestroy();
  offset = 0;
  previousValue: { startDate: Date; endDate: Date };
  constructor() {
    super();
  }

  ngOnInit() {
    if (this.props.maximalDaysRange === 1) {
      this.field.fieldGroup = this.field.fieldGroup.map(field => {
        if (field.key === 'endDate') {
          // hide endDate field if maximalDaysRange is 1
          field.props.hideDate = true;
          field.props.label = 'Tot';
        }
        if (field.key === 'startDate') {
          field.props.hideTimelabel = true;
        }
        return field;
      });
    }

    // in some cases it can be timestamp, convert to date
    if (this.field.formControl.value?.startDate != undefined && this.field.formControl.value?.endDate != undefined) {
      this.field.formControl.setValue({
        startDate: new Date(this.field.formControl.value?.startDate),
        endDate: new Date(this.field.formControl.value?.endDate),
      });
    }

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        debounceTime(100),
        tap(() => {
          if (this.props.maximalDaysRange === 1) {
            if (this.field.formControl.get('endDate').value == null) {
              return;
            }

            const endDate = new Date(this.field.formControl.get('endDate').value);
            const startDate = new Date(this.field.formControl.get('startDate').value);

            endDate.setFullYear(startDate.getFullYear());
            endDate.setMonth(startDate.getMonth());
            endDate.setDate(startDate.getDate());
            if (
              endDate.getHours() < startDate.getHours() ||
              (endDate.getHours() === startDate.getHours() && endDate.getMinutes() < startDate.getMinutes())
            ) {
              endDate.setDate(endDate.getDate() + 1);
            }

            const currentEndDate = this.field.formControl.get('endDate')?.value;

            if (currentEndDate != DateHelperService.toLocalISOString(endDate)) {
              this.field.formControl.get('endDate').setValue(DateHelperService.toLocalISOString(endDate));
            }
          }

          this.validateDates();
        })
      )
      .subscribe();
  }

  validateDates() {
    let startDate = this.field.formControl.get('startDate')?.value;
    let endDate = this.field.formControl.get('endDate')?.value;

    if (startDate && endDate) {
      // Compare only dates, ignoring time
      const startDateOnly = new Date(startDate).setHours(0, 0, 0, 0);
      const endDateOnly = new Date(endDate).setHours(0, 0, 0, 0);
      this.offset = endDateOnly - startDateOnly;

      if (this.offset < 0) {
        if (
          this.previousValue === undefined ||
          this.previousValue.startDate.getTime() !== new Date(startDate).getTime()
        ) {
          // Keep endDate time, only update the date part
          const newEndDate = new Date(endDate);
          startDate = new Date(startDate);
          newEndDate.setFullYear(startDate.getFullYear());
          newEndDate.setMonth(startDate.getMonth());
          newEndDate.setDate(startDate.getDate());

          this.formControl.patchValue({
            endDate: DateHelperService.toLocalISOString(newEndDate),
          });
        } else if (this.previousValue.endDate.getTime() !== new Date(endDate).getTime()) {
          // Keep startDate time, only update the date part
          const newStartDate = new Date(startDate);
          endDate = new Date(endDate);
          newStartDate.setFullYear(endDate.getFullYear());
          newStartDate.setMonth(endDate.getMonth());
          newStartDate.setDate(endDate.getDate());

          this.formControl.patchValue({
            startDate: DateHelperService.toLocalISOString(newStartDate),
          });
        }
      }
    } else if (startDate && !endDate) {
      this.formControl.patchValue({
        endDate: startDate,
      });
    } else if (!startDate && endDate) {
      this.formControl.patchValue({
        startDate: endDate,
      });
    }

    // Update previous value
    this.previousValue = {
      startDate: new Date(this.field.formControl.get('startDate')?.value),
      endDate: new Date(this.field.formControl.get('endDate')?.value),
    };
  }
}
